import { motion } from "framer-motion";
import { formatCurrency } from "../../../data/mock/dashboardData";

const W = 680;
const H = 252;
const PAD = { t: 24, r: 16, b: 36, l: 64 };
const innerW = W - PAD.l - PAD.r;
const innerH = H - PAD.t - PAD.b;

function scaleY(val, max) {
  return PAD.t + innerH - (val / max) * innerH;
}

function scaleX(i, len) {
  return PAD.l + (i / Math.max(1, len - 1)) * innerW;
}

function toPoints(values, max) {
  return values.map((v, i) => ({
    x: scaleX(i, values.length),
    y: scaleY(v, max),
  }));
}

function curlyPath(points, curl = 1.2) {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  let d = `M ${points[0].x},${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const d1 = Math.hypot(p1.x - p0.x, p1.y - p0.y) || 1;
    const d2 = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
    const d3 = Math.hypot(p3.x - p2.x, p3.y - p2.y) || 1;
    const t = 0.5 * curl;
    const cp1x = p1.x + ((p2.x - p0.x) * d2) / (d1 + d2) * t;
    const cp1y = p1.y + ((p2.y - p0.y) * d2) / (d1 + d2) * t;
    const cp2x = p2.x - ((p3.x - p1.x) * d2) / (d2 + d3) * t;
    const cp2y = p2.y - ((p3.y - p1.y) * d2) / (d2 + d3) * t;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

function formatPointAmount(value) {
  const amount = Number(value) || 0;
  if (Math.abs(amount) >= 1_000_000) return formatCurrency(amount);
  if (Math.abs(amount) >= 100) return `₵ ${Math.round(amount).toLocaleString()}`;
  return `₵ ${amount.toLocaleString("en-GH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatAxisAmount(value, max) {
  const absMax = Math.max(Math.abs(max), 0);
  if (absMax >= 1_000_000) {
    const millions = value / 1_000_000;
    return `₵${millions >= 10 ? Math.round(millions) : millions.toFixed(1)}M`;
  }
  if (absMax >= 1000) {
    const thousands = value / 1000;
    return `₵${thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)}k`;
  }
  if (absMax >= 100) return `₵${Math.round(value).toLocaleString()}`;
  if (absMax >= 10) return `₵${Math.round(value)}`;
  if (absMax >= 1) return `₵${value.toFixed(1)}`;
  return `₵${value.toFixed(2)}`;
}

function axisTicks(max) {
  const steps = 4;
  const ticks = [];
  const seen = new Set();
  for (let i = 0; i <= steps; i += 1) {
    const value = (max * i) / steps;
    const label = formatAxisAmount(value, max);
    if (seen.has(label)) continue;
    seen.add(label);
    ticks.push({ pct: i / steps, label });
  }
  return ticks;
}

function labelIndexes(length) {
  if (length <= 8) return new Set(Array.from({ length }, (_, index) => index));
  const target = 6;
  const indexes = new Set([0, length - 1]);
  const step = (length - 1) / (target - 1);
  for (let i = 1; i < target - 1; i += 1) indexes.add(Math.round(i * step));
  return indexes;
}

function curlyAreaPath(linePath, points, baselineY) {
  const first = points[0];
  const last = points[points.length - 1];
  return [
    linePath,
    `C ${last.x},${last.y + (baselineY - last.y) * 0.35} ${last.x},${baselineY - 4} ${last.x},${baselineY}`,
    `L ${first.x},${baselineY}`,
    `C ${first.x},${baselineY - 4} ${first.x},${first.y + (baselineY - first.y) * 0.35} ${first.x},${first.y}`,
    "Z",
  ].join(" ");
}

/**
 * @param {{ year?: number, labels?: string[], collected?: number[], target?: number[], subtitle?: string }} props
 */
export default function RecoveryTrendChart({
  year = new Date().getFullYear(),
  labels,
  collected,
  target,
  subtitle,
} = {}) {
  const COLLECTED = Array.isArray(collected) ? collected.map((n) => Number(n) || 0) : [];
  const hasData = COLLECTED.length > 0 && COLLECTED.some((v) => v > 0);
  const hasTarget = Array.isArray(target) && target.length === COLLECTED.length;
  const TARGET = hasTarget ? target.map((n) => Number(n) || 0) : [];
  const LABELS =
    Array.isArray(labels) && labels.length === COLLECTED.length
      ? labels
      : COLLECTED.map((_, i) => String(i + 1));

  if (!hasData) {
    return (
      <p className="flex min-h-[220px] items-center justify-center text-sm text-primary/40">
        No payments in this period yet.
      </p>
    );
  }

  const dataMax = Math.max(...COLLECTED, ...(TARGET.length ? TARGET : [0]), 0);
  const maxVal = (dataMax > 0 ? dataMax : 1) * 1.18;
  const collectedPts = toPoints(COLLECTED, maxVal);
  const targetPts = TARGET.length ? toPoints(TARGET, maxVal) : [];
  const curl = COLLECTED.length > 14 ? 0.45 : 1.05;
  const collectedLinePath = curlyPath(collectedPts, curl);
  const targetLinePath = targetPts.length ? curlyPath(targetPts, curl) : "";
  const baseline = scaleY(0, maxVal);
  const areaPath = curlyAreaPath(collectedLinePath, collectedPts, baseline);
  const peakIndex = COLLECTED.reduce(
    (best, value, index) => (value >= COLLECTED[best] ? index : best),
    0
  );
  const peakValue = COLLECTED[peakIndex];
  const peakLabel = formatPointAmount(peakValue);
  const badgeWidth = Math.max(72, peakLabel.length * 7.2);
  const badgeX = Math.min(
    Math.max(scaleX(peakIndex, COLLECTED.length) - badgeWidth / 2, PAD.l),
    W - PAD.r - badgeWidth
  );
  const badgeY = Math.max(4, scaleY(peakValue, maxVal) - 34);
  const periodLabel = subtitle || `Period ${year}`;
  const ticks = axisTicks(maxVal);
  const visibleLabels = labelIndexes(LABELS.length);
  const showEveryDot = COLLECTED.length <= 31;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={`Collection trend ${periodLabel}`}
      >
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#B91C1C" stopOpacity="0.22" />
            <stop offset="70%" stopColor="#B91C1C" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#B91C1C" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#B91C1C" />
            <stop offset="55%" stopColor="#DC2626" />
            <stop offset="100%" stopColor="#E5B80B" />
          </linearGradient>
          <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {ticks.map((tick) => {
          const y = PAD.t + innerH * (1 - tick.pct);
          return (
            <g key={tick.label}>
              <line
                x1={PAD.l}
                y1={y}
                x2={W - PAD.r}
                y2={y}
                stroke="#B91C1C"
                strokeOpacity="0.06"
                strokeDasharray="4 4"
              />
              <text
                x={PAD.l - 8}
                y={y + 3}
                textAnchor="end"
                fill="rgba(127,29,29,0.45)"
                fontSize="11"
              >
                {tick.label}
              </text>
            </g>
          );
        })}

        <motion.path
          d={areaPath}
          fill="url(#areaGrad)"
          stroke="none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
        {targetLinePath ? (
          <motion.path
            d={targetLinePath}
            fill="none"
            stroke="#E5B80B"
            strokeWidth="2"
            strokeDasharray="7 6"
            strokeOpacity="0.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.3, delay: 0.15 }}
          />
        ) : null}
        <motion.path
          d={collectedLinePath}
          fill="none"
          stroke="#B91C1C"
          strokeWidth="8"
          strokeOpacity="0.12"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#lineGlow)"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <motion.path
          d={collectedLinePath}
          fill="none"
          stroke="url(#lineGrad)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />

        {COLLECTED.map((value, index) => {
          const marked = showEveryDot || value > 0 || index === peakIndex;
          if (!marked) return null;
          const emphasized = index === peakIndex;
          return (
            <circle
              key={`${LABELS[index]}-${index}`}
              cx={scaleX(index, COLLECTED.length)}
              cy={scaleY(value, maxVal)}
              r={emphasized ? 5 : 3}
              fill={emphasized ? "#E5B80B" : "#B91C1C"}
              fillOpacity={emphasized ? 1 : 0.35}
              stroke="#fff"
              strokeWidth={emphasized ? 2.5 : 1.5}
            />
          );
        })}

        <g>
          <rect x={badgeX} y={badgeY} width={badgeWidth} height="22" rx="11" fill="#B91C1C" />
          <text
            x={badgeX + badgeWidth / 2}
            y={badgeY + 15}
            textAnchor="middle"
            fill="#fff"
            fontSize="11"
            fontWeight="700"
          >
            {peakLabel}
          </text>
        </g>

        {LABELS.map((label, index) =>
          visibleLabels.has(index) ? (
            <text
              key={`${label}-axis-${index}`}
              x={scaleX(index, LABELS.length)}
              y={H - 10}
              textAnchor={index === 0 ? "start" : index === LABELS.length - 1 ? "end" : "middle"}
              fill="rgba(127,29,29,0.55)"
              fontSize="11"
            >
              {label}
            </text>
          ) : null
        )}
      </svg>

      <div className="mt-1 flex flex-wrap items-center justify-between gap-3 text-[11px]">
        <div className="flex flex-wrap gap-4">
          <span className="flex items-center gap-1.5 text-primary/50">
            <span className="h-0.5 w-4 rounded-full bg-primary" />
            Collected
          </span>
          {hasTarget ? (
            <span className="flex items-center gap-1.5 text-primary/50">
              <span className="h-0.5 w-4 rounded-full border-t border-dashed border-accent bg-accent/40" />
              Target
            </span>
          ) : null}
        </div>
        <span className="text-primary/35">{periodLabel}</span>
      </div>
    </div>
  );
}
